/**
 * ccfacts.mjs — every Claude Code fact this ladder teaches, with the command that produced it.
 *
 * FOUNDER, 2026-08-15: "add a module in end for mastering claudecode zero to hero" and
 * "ensure course is world class and not paper and texty".
 *
 * ── WHY THIS FILE EXISTS AT ALL ───────────────────────────────────────────────────────────────
 * A course about a tool, written from an assistant's memory of that tool, is the wrapper failure
 * CodeAI101 chapter 1 teaches you to spot: fluent prose about flags and features, some of it stale,
 * none of it checkable. I am the fluent imitator. So not one fact below was recalled — each was
 * READ OFF THIS MACHINE, and each carries the command that produced it.
 *
 * gate-ccfacts.mjs RE-RUNS those commands and fails if an answer has moved. When Claude Code
 * updates and a flag disappears, the gate goes red and the course gets corrected — rather than
 * teaching, in confident prose, a flag that no longer exists. That is FP-018 applied to a course
 * about a tool: every claim is a QUOTE from the tool itself, and where the tool says nothing, the
 * course says nothing.
 *
 * ── WHAT IS DELIBERATELY NOT HERE ─────────────────────────────────────────────────────────────
 * No prices, no rate limits, no "Claude Code can do X better than Y", no performance claims. None
 * of those are readable from this machine, so none of them are taught. A course that stayed silent
 * where it could not verify is worth more than one that filled the gap.
 */

/** Read off this machine on the date below. The gate re-reads them. */
export const CAPTURED_ON = '2026-08-15';
export const REVERIFIED_ON = '2026-08-23 — against 2.1.241; the SEVENTH bump, and the first that skipped two because the red run was seen a day before it was fixed';
/* ── THE TOOL MOVED UNDER THE COURSE, MID-SESSION, AND THE GATE SAID SO ────────────────────────
   Captured against 2.1.232 on 2026-08-15. Hours later, in the same working session, Claude Code
   updated itself to 2.1.233 and gate-ccfacts went red on its next run — unprompted, with nobody
   watching for it. That is the entire argument for the design: a course about a tool decays
   silently unless something re-asks the tool.
   Re-verified on the bump rather than bumped: subcommands unchanged, all six permission modes
   present, every taught flag still in --help. Only the version string moved. */
/* ── AND IT HAPPENED AGAIN, 2026-08-18 ─────────────────────────────────────────────────────────
   2.1.233 → 2.1.234, mid-session, unprompted, with nobody watching for it — the second time this
   gate has caught the tool moving under the course. Re-VERIFIED rather than bumped: all thirteen
   subcommands still appear in --help, all six permission modes are still offered, every taught flag
   still exists. Only the version string moved.
   Twice is the argument. A course about a tool decays silently unless something re-asks the tool,
   and the interval between the two catches was three days. */
/* ── AND A THIRD TIME, 2026-08-19 ──────────────────────────────────────────────────────────────
   2.1.234 → 2.1.235, one day after the second. Re-VERIFIED rather than bumped, and the distinction
   is the whole point: on this run the gate re-read the live --help and confirmed every one of the
   thirteen subcommands, every verb of mcp/plugin/auto-mode/project, all six permission modes and
   the taught flag count BEFORE this line was touched. Only the version string moved.
   Three catches in four days. The number that matters is not 2.1.235, which will be wrong again
   shortly; it is the interval, and it is shortening.
   NOTE what is deliberately NOT synced with this: the sample outputs in chapters 1 and 17 still
   read 2.1.234, because chapter 1 tells the reader in as many words that its number is older than
   theirs and that the SHAPE is the lesson. Bumping a dated transcript to keep a number tidy would
   falsify a recorded example to no one's benefit. */
/* ── AND A FOURTH TIME, 2026-08-20 ─────────────────────────────────────────────────────────────
   2.1.235 → 2.1.236, one day after the third. Caught the same way as the others and by nobody
   looking for it: a full gate sweep run for an unrelated change came back 71 of 72, and the one red
   was this. Re-VERIFIED, not bumped — the gate re-ran the live commands and every other assertion
   in it passed against 2.1.236 before this line was touched. Only the version string moved.
   Four catches in five days, and the interval is now one day. The number here is not the fact worth
   holding; the fact worth holding is that a course about a tool cannot be written from memory of
   the tool, and this file is the mechanism rather than the claim. */
/* ── AND A FIFTH TIME, 2026-08-20 — TWICE IN ONE DAY ───────────────────────────────────────────
   2.1.236 → 2.1.237, hours after the fourth catch and again during a sweep run for something else.
   Re-VERIFIED, not bumped: 57 assertions in gate-ccfacts passed against 2.1.237 before this line
   was touched — every subcommand, every permission mode, every taught flag. Only the version moved.
   The interval was three days, then one, and now part of one. Nothing about the tool has broken;
   what has been demonstrated five times is that the recapture interval is not something a course
   can be written to assume. */
/* ── AND A SIXTH TIME, 2026-08-21 ─────────────────────────────────────────────────────────────
   2.1.237 → 2.1.238, caught overnight by the sweep at the end of a session about a maritime corpus
   — the third catch in a row that arrived while working on something else entirely, which is the
   argument for the gate rather than for a habit of checking. Re-VERIFIED, not bumped: the full
   gate-ccfacts run passed against 2.1.238 before this line was touched, and `doctor` still prints
   the update line in the shape chapter 17 quotes. Only the version moved; VERSION_DATE below is
   unchanged because the tool reports the same date, and half-syncing that pair is exactly the fault
   the note under it records. */
/* ── AND A SEVENTH TIME, 2026-08-23 — AND THIS ONE SKIPPED TWO ────────────────────────────────
   2.1.238 → 2.1.241. Not one bump: THREE, and the gate only ever showed the newest.

   What is worth recording is not the number, it is what happened in between. This gate went red on
   2026-08-22 at 2.1.239, was READ, was reported to the founder as "the one genuinely red gate", and
   was left — correctly, because re-stamping a course's facts is not a thing to do in passing at the
   end of a session about something else. By the time it was fixed on his instruction the tool had
   moved twice more. The gate never lied and never nagged; it simply stayed red, which is the whole
   behaviour a drift gate is for.

   RE-VERIFIED, not bumped, and the count is the evidence: SIXTY assertions in gate-ccfacts passed
   against the live 2.1.241 before this line was touched — every subcommand, every verb of
   mcp/plugin/auto-mode/project, all six permission modes, the taught flag count, and the shape of
   the `doctor` update line. Only the version and its date moved.

   BOTH HALVES MOVE THIS TIME, and they were read from ONE `doctor` run rather than assembled: the
   tool printed "Last update attempt: success → 2.1.241 (2026-08-23)". The note under VERSION_DATE
   records what happens when that pair is half-synced, and it is the reason the pair is captured in
   one observation rather than two.

   Seven catches in nine days, and the interval is now shorter than the gap between the failure
   being seen and being fixed. That is the argument against ever writing this course from memory of
   the tool — and, this time, an argument for fixing a red gate as its own act rather than folding
   it into whatever session happened to find it. */
export const VERSION = '2.1.241';
export const VERSION_CMD = 'claude --version';
/* THE DATE BESIDE THE VERSION, AND WHY IT IS DECLARED HERE RATHER THAN LEFT IN THE PROSE.
   `doctor` prints "success → <version> (<date>)". Chapter 17 quotes that line twice as a worked
   example. On the fourth bump sync-counts rewrote the VERSION inside those transcripts and left the
   DATE alone — it had no way to know it — and published "2.1.236 (2026-08-18)", an update that
   never happened, assembled out of two facts that were each true. Half-syncing a transcript
   manufactures a false record more convincingly than never syncing it at all.
   sync-counts is deliberately a pure file transformer that never probes the tool, so the fix is not
   to teach it to run `doctor`; it is to declare the observed pair here, where every other captured
   fact already lives, and let it write both halves together. gate-ccfacts asserts the pair against
   the live tool, so a stale half is caught rather than trusted. */
export const VERSION_DATE = '2026-08-23';
export const VERSION_DATE_CMD = 'claude doctor';

/**
 * The subcommands, exactly as `claude --help` lists them. Not a curated selection — the whole set,
 * because a course that showed five of fourteen would be teaching a shape the tool does not have.
 */
export const COMMANDS = Object.freeze([
  'agents', 'auth', 'auto-mode', 'doctor', 'gateway', 'import', 'install',
  'mcp', 'plugin', 'project', 'setup-token', 'ultrareview', 'update',
]);
export const COMMANDS_CMD = 'claude --help';

/**
 * THE PERMISSION DIAL — the load-bearing fact of this whole ladder.
 *
 * CodeAI101 chapter 3 taught three tiers of approval as a concept. Claude Code implements SIX, and
 * they are read here from `claude --help` rather than from anybody's description of them. The
 * mapping from the three-tier concept to these six is the lab's own reading and is marked as such;
 * the six names are the tool's.
 */
export const PERMISSION_MODES = Object.freeze([
  'acceptEdits', 'auto', 'bypassPermissions', 'manual', 'dontAsk', 'plan',
]);
export const PERMISSION_MODES_CMD = 'claude --help  (--permission-mode choices)';

/** Directories the tool really keeps under ~/.claude, filtered to the ones a learner meets. */
export const HOME_LAYOUT = Object.freeze([
  { name: 'CLAUDE.md', what: 'instructions loaded into every session in this scope' },
  { name: 'settings.json', what: 'permissions, hooks, model, statusline — the tool\'s configuration' },
  { name: 'skills', what: 'packaged instruction sets it can invoke by name' },
  { name: 'agents', what: 'subagent definitions it can delegate to' },
  { name: 'hooks', what: 'scripts fired on tool events, before or after' },
  { name: 'projects', what: 'one transcript per session, as JSONL, on your own disk' },
  { name: 'plugins', what: 'installed plugin bundles' },
  { name: 'rules', what: 'path-scoped instruction modules' },
]);
export const HOME_LAYOUT_CMD = 'ls ~/.claude';

/** The settings.json keys in real use on this machine — a real config, not a documentation sample. */
export const SETTINGS_KEYS = Object.freeze([
  'permissions', 'hooks', 'statusLine', 'effortLevel', 'tui',
  'skipWorkflowUsageWarning', 'theme', 'skipAutoPermissionPrompt', 'model',
]);
export const SETTINGS_KEYS_CMD = 'keys of ~/.claude/settings.json';

/**
 * A SELECTION of flags, chosen because each one teaches something a learner needs. The full list is
 * long and the gate checks every one of these still exists — but this IS a selection and says so,
 * unlike COMMANDS above which is exhaustive. A course that presented a curated subset as the whole
 * would be lying by shape rather than by fact.
 */
export const TAUGHT_FLAGS = Object.freeze([
  /* The flag gate-ccfacts itself runs to keep VERSION honest — chapter 1 teaches it as step 2,
     because an installer reporting success is not the program reporting that it runs. */
  { flag: '--version', teaches: 'confirm the program runs at all, independently of what the installer claimed' },
  { flag: '--permission-mode', teaches: 'which of the six dial positions this session starts in' },
  { flag: '--model', teaches: 'which model answers — the tool is not one fixed brain' },
  { flag: '--print', teaches: 'run it non-interactively and get the answer on stdout, for scripts' },
  { flag: '--max-budget-usd', teaches: 'a real cost limit, which is CodeAI101 chapter 4 made concrete' },
  { flag: '--add-dir', teaches: 'widen what it is allowed to see beyond the working directory' },
  /* NOT `--allowed` / `--disallowed`. Those were taught here for weeks and neither exists; they
     survived because gate-ccfacts matched flags as SUBSTRINGS, and `--allowed` is a substring of
     `--allowedTools`. The gate now requires a whole-token match and carries a control that is a
     prefix of a real flag. Read off `claude --help` 2026-08-16: both spellings are on one line. */
  { flag: '--allowed-tools', teaches: 'name the tool CALLS permitted without asking, as patterns like "Bash(git *)"' },
  { flag: '--disallowed-tools', teaches: 'name the tool calls to deny' },
  { flag: '--tools', teaches: 'choose which tools EXIST for this session at all — "" for none, or "Bash,Edit,Read"' },
  { flag: '--verbose', teaches: 'see what it is actually doing rather than the summary' },
  { flag: '--fork-session', teaches: 'branch a session instead of continuing it' },
  { flag: '--settings', teaches: 'point it at a different configuration entirely' },
]);
export const FLAGS_CMD = 'claude --help';
/* ── A SELF-COUNT SPELLED IN WORDS IS INVISIBLE TO THE COMPILER THAT OWNS IT ─────────────────
   This read "These ten are a teaching selection" and was exactly right on 2026-08-15, when
   TAUGHT_FLAGS held ten. Two were added since — the `--allowed-tools` / `--disallowed-tools`
   correction among them — and the sentence never moved, because sync-counts.mjs matches DIGITS and
   there were none here to match. The count was not hand-kept by choice; it was hand-kept because
   nothing could see it. Spelling a number as a word takes it out of the compiler's reach while
   leaving it looking exactly like the compiled ones beside it.
   Derived from the array now, so the sentence cannot disagree with the list it describes. */
export const FLAGS_ARE_A_SELECTION = `These ${TAUGHT_FLAGS.length} are a teaching selection, not the whole flag list — the tool has many more. The subcommand list above IS complete.`;

/**
 * WHAT THIS LADDER REFUSES TO TEACH.
 *
 * Each of these is a thing a learner will want to know and a thing this machine cannot tell me. The
 * honest move is to name the silence rather than fill it, which is the same discipline the tutor
 * applies to a dosage it cannot read off a QR code.
 */
export const REFUSED = Object.freeze([
  { q: 'What does it cost to run?', why: 'Not readable from this machine. Pricing changes and varies by plan; the course would be quoting a number it cannot check.' },
  { q: 'Which model is best for my work?', why: 'A recommendation, not a fact. The tool reports which models exist, not which suits your task.' },
  { q: 'How does it compare to other coding assistants?', why: 'Not measurable here, and ANKR does not build itself up by naming others (FP-019).' },
  { q: 'What are the rate limits?', why: 'Account-specific and not readable from the CLI.' },
]);

/**
 * ── A MEASURED COST IS NOT A PRICE (2026-08-16) ───────────────────────────────────────────────
 * REFUSED above says the course will not quote what the tool costs, and that stands: a price list
 * is account-specific, changes without notice, and is not readable from this machine.
 *
 * What IS readable is what a particular run cost, because the tool reports it in its own JSON. That
 * is a measurement with provenance, not a price — the same distinction the whole ladder rests on
 * (FP-018: computed or quoted, never guessed). Chapter 10 needs it, because the honest thing to say
 * about `--max-budget-usd` cannot be said without a number: a ten-cent ceiling did not buy a
 * ten-cent maximum, and only the figure shows how far off it was.
 *
 * Declared here so gate-claudecode101 can hold the line properly — a dollar amount in the prose is
 * allowed ONLY if it appears below, and anything rate-shaped ("per million", "a month") is refused
 * outright however it is worded. Read off `total_cost_usd` in the run's own output envelope.
 */
export const MEASURED_COSTS = Object.freeze({
  captured_on: '2026-08-16',
  last_measured: '2026-08-27',
  field: 'total_cost_usd, from the run\'s own --output-format json envelope',
  caveat: 'One machine, one account, one standing context. Your figures will differ and the point is the SHAPE, not the amount.',
  drift: 'The 2026-08-27 re-run is the SAME command as the first two, eleven days later, and cost a third more. A cost figure without a date is a cost figure you cannot check.',
  runs: Object.freeze([
    { what: 'one-line prompt, --max-budget-usd 0.10', cost: 0.617235, turns: 1, ended: 'budget_exhausted', measured: '2026-08-16' },
    { what: 'the same, run again', cost: 0.621105, turns: 1, ended: 'budget_exhausted', measured: '2026-08-16' },
    { what: 'the same again, eleven days later', cost: 0.80224, turns: 1, ended: 'budget_exhausted', measured: '2026-08-27' },
    { what: 'one-line prompt with --json-schema, --tools "", --max-budget-usd 3', cost: 1.06407, turns: 2, ended: 'success', measured: '2026-08-16' },
  ]),
  what_it_shows: 'The ceiling is checked BETWEEN turns, so the least it can cost is one whole turn. --max-budget-usd bounds the number of turns, not the bill.',
});

/**
 * THE CAPSTONE MATERIAL. This ladder's last chapter is a real build with its real mistakes, and
 * these are its measured facts. The mistakes are the point: a zero-to-hero course whose worked
 * example went perfectly would be teaching that it goes perfectly.
 */
export const THE_BUILD = Object.freeze({
  what: 'The academy you are reading this in — CodeAI101\'s seven interactive labs, built in one working session.',
  labs: 34,
  /* IT COUNTS ITSELF, and it keeps catching new arrivals. First recorded as 35 before
     gate-ccfacts.mjs existed; the gate caught its own arrival on the first run. 60 -> 61 on
     2026-08-17 when gate-chapter-hull.mjs was added for the chapter/hull wiring — the gate went red
     inside the same session that created the cause, which is the whole reason this number is
     re-measured rather than remembered.
     62 -> 68 on 2026-08-18, six gates in one day — catalogue, chapter-atlas, module-prose and
     understand-rungs among them — every one written to guard work done the same afternoon. The
     gate went red on its own author's productivity, which is exactly what it is for: a number
     about the estate is only true on the day it was counted.
     87 -> 88 on 2026-08-24: gate-capacity-plan, guarding M.V. SAARTHI's capacity plan and her twelve
     loading conditions. Caught by a full sweep run for that work, in TWO gates at once — ccfacts
     against the live directory and claimsort against the ClaimLab prose — which is the pair working
     as designed: the fact and the sentence that quotes it cannot drift apart quietly. */
  gates: 91,
  measured_on: '2026-08-27',
  mistakes_worth_teaching: Object.freeze([
    { id: 'elision', what: 'Seven times, a quotation was shortened to fit a slot — a parenthetical dropped, a sentence ended early.',
      caught_by: 'Gates that matched each quote against the source module rather than against the assistant\'s memory of it.',
      lesson: 'An assistant will trim a quotation to make it fit. Check quotes against the source, mechanically.' },
    { id: 'prose-match', what: 'Six times, a check that grepped source code for a forbidden word matched the file\'s own comments — including a promise NOT to do the thing.',
      caught_by: 'The check going red on correct code, which is how the fault announced itself.',
      lesson: 'Test the shape of the code, not the presence of a word. Prose contains the words code uses.' },
    { id: 'flattering-count', what: 'A self-audit counted every file twice and reported roughly double.',
      caught_by: 'A gate that re-took the measurement independently.',
      lesson: 'Re-measure rather than trust a recorded number — especially a flattering one.' },
    { id: 'inert-test', what: 'A test written to prove a gate could fail was itself inert — it changed a branch that never ran.',
      caught_by: 'Checking whether the sabotage had actually applied, instead of concluding the gate had missed it.',
      lesson: 'When a deliberate break does not go red, first ask whether the break happened.' },
    { id: 'vacuous-law', what: 'A gate law was tested only on the path where it could not fail, and stayed green against the exact fault it existed to catch.',
      caught_by: 'Deliberately breaking the thing the law protected.',
      lesson: 'A law tested only where it cannot fail is decoration. Break it on purpose and watch.' },
    /* ── THE SIXTH LIVED ONLY IN THE CHAPTER, WHICH IS THE FAULT THIS FILE EXISTS TO REMOVE ────
       Chapter 20 teaches SIX kinds of mistake and this list held five, so its headline count was
       asserted by prose rather than quoted from a record — in the capstone of a ladder whose whole
       argument is that a number should come from the thing it counts. The five that were here are
       the ones a gate caught; this one is the one a PERSON caught, three times in three days, and
       leaving it out quietly made the record look like a record of everything.
       It is also the most expensive of the six, so its absence was the least affordable. */
    { id: 'verified-the-wrong-artefact', what: 'Three times in three days, a fix was verified against the thing just written — the API, the markdown file, the bundle — while the thing the user receives stayed broken by a layer in between.',
      caught_by: 'The founder, each time. No gate opened the page.',
      lesson: 'Verify what the user receives, not what you produced. Something always sits between them: a compiler, a cache, a proxy, a service that has to be running.' },
  ]),
});

/**
 * ── CHAPTER 3 · WHAT A SESSION CAN SEE ────────────────────────────────────────────────────────
 * Read off this machine. The point a learner needs: a session is NOT empty when you start typing.
 * Instructions are already in the window, and you did not put them there this morning.
 */
export const CONTEXT_FACTS = Object.freeze({
  claude_md: Object.freeze([
    { path: '~/.claude/CLAUDE.md', bytes: 226, lines: 3, scope: 'every session for this user, in any directory' },
    { path: './CLAUDE.md', bytes: 12105, lines: 109, scope: 'every session started in this project' },
  ]),
  path_scoped_rules: 8,
  rules_dir: '~/.claude/rules',
  compactions_this_session: 3,
  cmd: 'wc -c on each CLAUDE.md; ls ~/.claude/rules; grep for compact records in the transcript',
});
export const WHAT_COMPACTION_IS = 'When a conversation outgrows the window, the older part is summarised and the summary replaces it. The work continues. What was said verbatim is gone, and only what the summary chose to carry survives.';

/**
 * ── CHAPTER 4 · THE FOUR BOUNDARIES, AGAINST WHAT THIS TOOL ACTUALLY HAS ──────────────────────
 * CodeAI101 chapter 4 named four boundaries: a clear goal, an attempt limit, a cost limit, a time
 * limit. ONE of the four has a flag here, and it is restricted to headless runs.
 *
 * This list said "three of those four are flags on this tool". It got there by swapping the attempt
 * limit and the time limit for two controls this tool DOES have — what it may use, what it may see
 * — and then counting three. No single row was false and the count was manufactured: the question
 * had been changed to one with a better answer. Corrected 2026-08-16 against `claude --help`.
 */
export const BOUNDING = Object.freeze([
  { boundary: 'A cost limit', flag: '--max-budget-usd', real: true, only_with: '--print',
    note: 'A ceiling in money — and the tool\'s own clause says it only works with --print. There is no cost cap on an interactive session.' },
  { boundary: 'An attempt limit', flag: null, real: false,
    note: 'No flag. `claude --help` names no turn or attempt limit of any kind.' },
  { boundary: 'A time limit', flag: null, real: false,
    note: 'No flag. Nothing in --help bounds a run by duration.' },
  { boundary: 'A clear goal', flag: null, real: false,
    note: 'No flag exists for this and none could. CodeAI101 chapter 4 calls a vague goal the boundary that decides whether the agent can ever finish — and it is the one you have to supply yourself, every time.' },
]);
/** What this tool bounds that CodeAI101's list did not name. Kept separate so it can never again be
 *  counted as though it filled one of the four gaps above. */
export const BOUNDING_EXTRA = Object.freeze([
  { bounds: 'Which tools exist at all', flag: '--tools',
    note: 'A capability cap rather than a permission rule — a tool absent from the set cannot be called, approved by accident, or appear in a plan.' },
  { bounds: 'Which tool calls need no approval', flag: '--allowed-tools / --disallowed-tools',
    note: 'Patterns, not bare names: "Bash(git *)" permits git specifically rather than Bash generally.' },
  { bounds: 'Which directories are in scope', flag: '--add-dir',
    note: 'Adds to the working directory rather than replacing it, so the real decision is where you started.' },
]);
export const PERMISSIONS_ON_THIS_MACHINE = Object.freeze({
  allow_rules: 7, default_mode: 'auto',
  cmd: 'keys and lengths of permissions in ~/.claude/settings.json',
});

/**
 * ── CHAPTER 5 · WHAT IS ACTUALLY INSTALLED HERE ───────────────────────────────────────────────
 * Counted on this machine. A learner reading "you can add hooks" learns less than a learner seeing
 * that this machine has fifteen chances to intercept a tool call before it runs.
 */
export const EXTENSIONS = Object.freeze({
  skills: 4, skill_names: Object.freeze(['claw-audit', 'graphify', 'named-consent', 'tenet']),
  agents: 2,
  hooks: Object.freeze([
    { event: 'PreToolUse', matchers: 15, when: 'before a tool runs — the only place that can still stop it' },
    { event: 'PostToolUse', matchers: 7, when: 'after a tool ran — too late to prevent, in time to record' },
    { event: 'SessionStart', matchers: 1, when: 'as a session opens' },
    { event: 'UserPromptSubmit', matchers: 1, when: 'as you press enter' },
    { event: 'Notification', matchers: 1, when: 'when it wants your attention' },
    { event: 'Stop', matchers: 1, when: 'when it finishes a turn' },
  ]),
  cmd: 'ls ~/.claude/skills; ls ~/.claude/agents; hooks in ~/.claude/settings.json',
});
export const WHY_PRETOOLUSE_MATTERS = 'PreToolUse is CodeAI101 chapter 3\'s approval gate, made programmable. Everything else observes; this one can refuse.';

/**
 * ── CHAPTER 6 · THE VERIFICATION DISCIPLINE, COUNTED ──────────────────────────────────────────
 * Counted across this repository. These are the numbers the mastery chapter is about.
 */
/* ── ONE FACT, ONE PLACE ───────────────────────────────────────────────────────────────────
   The gate count lived here AND in THE_BUILD, and an update to one left the other stale — the
   prose said 40 while this still said 39, and only the prose gate noticed. Two records of one
   fact is how they come to disagree, which is the principle the whole codex rests on. Derived
   from THE_BUILD now, so there is nothing to keep in sync. */
/* ── AND THE SAME FAULT AGAIN, ONE FIELD OVER (2026-08-16) ──────────────────────────────────
   The comment above fixed `gates` by deriving it. `gates_with_controls` and `control_assertions`
   were left as literals and went stale exactly the same way — 35 and 337 here against 53 and 471
   live. A gate compared the prose to THESE, so chapters 16 and 20 quoting the true figures went
   RED for being right. Fixing one instance of a fault is not fixing the fault: the other two
   fields were sitting in the same object, written the same day, for the same reason.
   Now recomputed by sync-counts.mjs, which writes them here and into both chapters from one
   measurement. `gates_that_bail` stays a literal because it is a judgement about what a gate does
   when it cannot verify, not something a regex can count — and it is marked as such rather than
   left looking like the others. */
export const VERIFY_FACTS = Object.freeze({
  get gates() { return THE_BUILD.gates; },
  gates_with_controls: 80, control_assertions: 691,
  gates_that_bail: 8, gates_that_bail_is_hand_counted: true,
  cmd: 'ls and grep over slm-tutor/gate-*.mjs',
});
export const THE_DISCIPLINE = 'A gate you have never seen fail is not a gate — it is a green light with no wiring behind it. Break the thing it protects, watch it go red, then put it back.';

/**
 * ── THE DEEP-DIVE FACT BASE (2026-08-15) ──────────────────────────────────────────────────────
 * FOUNDER: "i want claude code chapters to deep dive, freecodecamp was 25 chapters for claudecode".
 *
 * Nine chapters taught 10 flags of 65 and named 13 subcommands without explaining one. The ground
 * for real depth was already on the machine and unused. Everything below was read from `claude
 * <cmd> --help` and is re-verified by gate-ccfacts.mjs, exactly as the first set is.
 *
 * What is deliberately NOT added, though freeCodeCamp's handbook has chapters on them: company
 * background, community context, and subscription tiers. The first two are not this tool and the
 * third is pricing, which this course refuses because it cannot check it.
 */
export const SUBCOMMANDS = Object.freeze([
  /* ELEVEN, read off `claude mcp --help` 2026-08-16. This said six, chapter 6 said ten, and the
     tool has eleven — three records of one fact, all different, and nothing caught it because the
     verb check only ran ONE WAY: it asked whether every verb the course names still exists, never
     whether the tool had gained one. A new verb was therefore invisible by construction. The reverse
     check is now applied to verbs as it already was to the subcommand list itself. */
  { name: 'mcp', teaches: 'connect servers that give the tool capabilities it did not ship with',
    verbs: ['add', 'add-from-claude-desktop', 'add-json', 'get', 'help', 'list', 'login', 'logout',
            'remove', 'reset-project-choices', 'serve'] },
  /* FOURTEEN, read live 2026-08-16. Declared as seven until the verb check was made to run in both
     directions; the other seven — help, list, marketplace, prune, tag, update, validate — had been
     invisible because nothing ever asked what the tool had GAINED. */
  { name: 'plugin', teaches: 'package skills, agents and hooks into something installable',
    verbs: ['details', 'disable', 'enable', 'eval', 'help', 'init', 'install', 'list', 'marketplace',
            'prune', 'tag', 'uninstall', 'update', 'validate'] },
  /* The flags used to be listed inline here as well as in AGENT_VIEW_FLAGS above — two records of
     one fact, and the inline copy was read by nothing, which is the worst kind: unverified AND
     unused, sitting there looking authoritative. Removed rather than synchronised. */
  { name: 'agents', teaches: 'a view for dispatching and watching background sessions', verbs: [] },
  { name: 'auto-mode', teaches: 'inspect and reset the classifier that decides when it asks you',
    verbs: ['config', 'critique', 'defaults', 'help', 'reset'] },
  { name: 'project', teaches: 'delete all state for a project', verbs: ['help', 'purge'] },
  { name: 'doctor', teaches: 'check the health of the installation, without a trust prompt', verbs: [] },
  { name: 'import', teaches: 'bring configuration over from another agent', verbs: [], note: 'sources: codex, gemini' },
  { name: 'update', teaches: 'move to a newer build', verbs: [] },
  { name: 'install', teaches: 'install a native build, optionally a specific version', verbs: [] },
  { name: 'auth', teaches: 'manage authentication', verbs: [] },
  { name: 'setup-token', teaches: 'create a long-lived token for non-interactive use', verbs: [] },
  { name: 'gateway', teaches: 'run the enterprise auth and telemetry gateway', verbs: [] },
  { name: 'ultrareview', teaches: 'a deeper review pass', verbs: [] },
]);

/** Session control — the flags that decide whether you continue, branch, or start clean. */
export const SESSION_FLAGS = Object.freeze([
  { flag: '-c, --continue', teaches: 'continue the most recent conversation' },
  { flag: '--resume', teaches: 'pick a session to resume by id' },
  { flag: '--fork-session', teaches: 'when resuming, branch to a NEW session id rather than extending the old one' },
  { flag: '--session-id', teaches: 'name the session yourself, so a script can find it again' },
  { flag: '--no-session-persistence', teaches: 'leave no session behind at all' },
]);

/** Headless operation — the flags that make it a program rather than a conversation. */
export const HEADLESS_FLAGS = Object.freeze([
  { flag: '-p, --print', teaches: 'run once, print the answer, exit — no interactive session' },
  { flag: '--output-format', teaches: 'text, json, or stream-json for reading as it arrives' },
  { flag: '--input-format', teaches: 'how what you pipe in is to be read' },
  { flag: '--json-schema', teaches: 'require the answer to match a schema you supply' },
  { flag: '--include-partial-messages', teaches: 'see tokens as they stream, not only the finished reply' },
  { flag: '--replay-user-messages', teaches: 'echo what was sent, so a log holds both halves' },
]);

/**
 * ── THE AGENT VIEW'S OWN FLAGS (read off `claude agents --help`, 2026-08-16) ──────────────────
 * A SUBCOMMAND's flags, not the session's, and that is the point chapter 12 is built on: every one
 * of these configures the session that gets DISPATCHED rather than the one you are sitting in. A
 * careful main session that dispatches carelessly has been careful about the wrong thing.
 *
 * The two bypass flags are kept together because nine characters separate them and the difference
 * is whether an unattended worker runs with no permission layer or is merely allowed to.
 */
export const AGENT_VIEW_FLAGS = Object.freeze([
  { flag: '--permission-mode', teaches: 'the dial position a DISPATCHED session starts in — not necessarily yours' },
  { flag: '--dangerously-skip-permissions', teaches: 'defaults dispatched sessions to bypass' },
  { flag: '--allow-dangerously-skip-permissions', teaches: 'makes bypass AVAILABLE to them without defaulting to it' },
  { flag: '--json', teaches: 'print active sessions as JSON and exit — no TTY needed, so a script can answer "what is running"' },
  { flag: '--add-dir', teaches: 'extra directories a dispatched session may reach' },
  { flag: '--effort', teaches: 'how hard a dispatched session thinks' },
]);
export const AGENT_VIEW_FLAGS_CMD = 'claude agents --help';

/**
 * ── `claude mcp add`'s OWN FLAGS (read off `claude mcp add --help`, 2026-08-16) ────────────────
 * A verb's flags, one level below a subcommand's, and the two defaults are the chapter-13 content:
 * both choose the wider thing when you say nothing, which is the direction defaults usually go.
 */
export const MCP_ADD_FLAGS = Object.freeze([
  { flag: '--transport', teaches: 'remote or local — and it DEFAULTS to stdio, a command on your own machine' },
  { flag: '--scope', teaches: 'who inherits the server — defaults to local; `project` is checked in and arrives for everyone who clones' },
  { flag: '--env', teaches: 'environment variables for a local server, which is how a secret reaches one' },
  { flag: '--header', teaches: 'headers for a remote server, which is the other way a secret reaches one' },
]);
export const MCP_ADD_FLAGS_CMD = 'claude mcp add --help';

/**
 * ── `claude auto-mode`'s VERB FLAGS (read off each verb's own --help, 2026-08-16) ──────────────
 * Chapter 15 needs these to be checkable for the same reason every other flag is: the classifier is
 * the one control on the ladder whose behaviour cannot be predicted from its name, so the commands
 * that READ it are the whole of what a learner can rely on.
 */
export const AUTO_MODE_FLAGS = Object.freeze([
  { verb: 'defaults', flag: '--label', teaches: 'show only rules whose label starts with a prefix — how you read one rule out of eighty-four' },
  { verb: 'critique', flag: '--model', teaches: 'ask a different model for the second reading' },
  { verb: 'reset', flag: '--yes', teaches: 'skip the confirmation — the flag not to reach for, since reset EDITS your settings file' },
]);
export const AUTO_MODE_FLAGS_CMD = 'claude auto-mode <verb> --help';

/** What a session may be told to use or refuse, beyond the permission dial. */
export const SCOPE_FLAGS = Object.freeze([
  { flag: '--tools', teaches: 'name the tool set for this session' },
  { flag: '--agents', teaches: 'define custom agents inline as JSON' },
  { flag: '--mcp-config', teaches: 'point at a specific MCP configuration' },
  { flag: '--strict-mcp-config', teaches: 'use ONLY the servers in that config, ignoring the rest' },
  { flag: '--plugin-dir', teaches: 'load plugins from a named directory, for this session and any it dispatches' },
  { flag: '--setting-sources', teaches: 'choose which of user, project and local settings load' },
  { flag: '--append-system-prompt', teaches: 'add to the system prompt rather than replacing it' },
  { flag: '--system-prompt', teaches: 'replace it' },
  { flag: '--effort', teaches: 'how hard to think, per session' },
  { flag: '--fallback-model', teaches: 'what to use when the first choice is unavailable' },
]);

export const FLAG_COUNT = 65;          // `claude --help`, counted 2026-08-15
export const FLAGS_TAUGHT_BEFORE = 10; // what the nine-chapter version covered
